import React from 'react';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import { Button, SearchableSelect, CardContainer, LayoutGrid as LayoutGridComponent, LayoutMain, LayoutSide, useGraphQLQuery, Icon, SectionHeading } from '@tviklax/ui-components';
import { useRole } from '../../../context/RoleContext';
import { Contact, ShipmentContacts } from '../../../shared/types/contact';
import { ContactPanel } from '../../../shared/components/ContactPanel/ContactPanel';
import { BookShipment } from './BookShipment';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const Breadcrumb = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray600};
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Crumb = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[3]};
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 12;
  background: rgba(248, 250, 252, 0.95);
  backdrop-filter: blur(6px);
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: flex-start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const Label = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-weight: 700;
`;

const Value = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: 600;
`;


const Pill = styled.span<{ $tone?: 'in-transit' | 'arrived' | 'delivered' | 'delayed' | 'pending' | 'cleared' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $tone }) => {
    if ($tone === 'delivered') return '#ecfdf3';
    if ($tone === 'in-transit') return '#e0f2fe';
    if ($tone === 'arrived') return '#eef2ff';
    if ($tone === 'delayed') return '#fef2f2';
    if ($tone === 'pending') return '#fff7ed';
    if ($tone === 'cleared') return '#ecfdf3';
    if ($tone === 'info') return '#e0f2fe';
    return '#f3f4f6';
  }};
  color: ${({ $tone }) => {
    if ($tone === 'delivered') return '#065f46';
    if ($tone === 'in-transit') return '#075985';
    if ($tone === 'arrived') return '#4338ca';
    if ($tone === 'delayed') return '#991b1b';
    if ($tone === 'pending') return '#b45309';
    if ($tone === 'cleared') return '#065f46';
    if ($tone === 'info') return '#075985';
    return '#4b5563';
  }};
`;

const Badge = styled.span<{ $variant?: 'info' | 'warning' | 'success' | 'danger' }>`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $variant }) => {
    if ($variant === 'warning') return '#fef3c7';
    if ($variant === 'success') return '#d1fae5';
    if ($variant === 'danger') return '#fee2e2';
    return '#e0f2fe';
  }};
  color: ${({ $variant }) => {
    if ($variant === 'warning') return '#92400e';
    if ($variant === 'success') return '#065f46';
    if ($variant === 'danger') return '#991b1b';
    return '#075985';
  }};
`;

const Inline = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const TimelineItem = styled.div`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const TimelineTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const TimelineLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.03em;
`;

const TimelineValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray800};
  display: flex;
  flex-direction: column;
  gap: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 0;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.gray600};
  font-size: 13px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.gray200};
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.6fr 0.7fr 0.7fr;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
`;

const TableHeader = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray600};
  font-weight: 700;
`;

const TableRow = styled.div`
  display: contents;
`;

const AlertBox = styled.div<{ $severity: 'info' | 'warning' | 'danger' }>`
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ $severity }) => {
    if ($severity === 'warning') return '#fffbeb';
    if ($severity === 'danger') return '#fef2f2';
    return '#eff6ff';
  }};
  border-left: 4px solid ${({ $severity }) => {
    if ($severity === 'warning') return '#f59e0b';
    if ($severity === 'danger') return '#dc2626';
    return '#3b82f6';
  }};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AlertTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Intent = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray700};
  max-width: 720px;
`;

// --- Summary header, stepper, and at-a-glance cards -----------------------

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeaderStatusChip = styled.span<{ $tone: 'in-transit' | 'arrived' | 'delivered' | 'pending' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $tone }) => ($tone === 'delivered' ? '#ecfdf3' : $tone === 'in-transit' ? '#fff7ed' : $tone === 'arrived' ? '#eef2ff' : '#f3f4f6')};
  color: ${({ $tone }) => ($tone === 'delivered' ? '#065f46' : $tone === 'in-transit' ? '#c2410c' : $tone === 'arrived' ? '#4338ca' : '#4b5563')};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.gray900};
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray600};
`;

const StepperRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing[2]};
  overflow-x: auto;

  @media (max-width: 768px) {
    grid-auto-flow: column;
    grid-auto-columns: minmax(140px, 1fr);
  }
`;

const StepperItem = styled.div<{ $state: 'done' | 'active' | 'todo' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1.5px solid ${({ theme, $state }) => ($state === 'active' ? theme.colors.primary : $state === 'done' ? theme.colors.primary200 : theme.colors.border)};
  background: ${({ theme, $state }) => ($state === 'todo' ? theme.colors.surface : theme.colors.primary50)};
`;

const StepCircle = styled.div<{ $state: 'done' | 'active' | 'todo' }>`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $state }) => ($state === 'todo' ? theme.colors.surface : theme.colors.primary)};
  border: 2px solid ${({ theme, $state }) => ($state === 'todo' ? theme.colors.borderStrong : theme.colors.primary)};
  color: white;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const StepLabel = styled.div<{ $state: 'done' | 'active' | 'todo' }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme, $state }) => ($state === 'todo' ? theme.colors.gray500 : theme.colors.gray900)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StepMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray500};
  white-space: nowrap;
`;

const PrimaryAlertBanner = styled.div<{ $severity: 'info' | 'warning' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 18px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ $severity }) => ($severity === 'danger' ? '#fef2f2' : $severity === 'warning' ? '#fff7ed' : '#eff6ff')};
`;

const PrimaryAlertLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const PrimaryAlertIcon = styled.div<{ $severity: 'info' | 'warning' | 'danger' }>`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: ${({ $severity }) => ($severity === 'danger' ? '#dc2626' : $severity === 'warning' ? '#f59e0b' : '#3b82f6')};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PrimaryAlertText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray800};
  min-width: 0;

  strong {
    color: ${({ theme }) => theme.colors.gray900};
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryCardTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const SummaryCardCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray500};
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventItem = styled.div<{ $isFirst?: boolean }>`
  position: relative;
  padding: 0 0 14px 20px;
  border-left: 2px solid ${({ theme }) => theme.colors.gray200};

  &:last-child {
    border-left-color: transparent;
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme, $isFirst }) => ($isFirst ? theme.colors.primary : theme.colors.gray300)};
    border: 2px solid ${({ theme }) => theme.colors.surface};
  }
`;

const EventDate = styled.div<{ $isFirst?: boolean }>`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme, $isFirst }) => ($isFirst ? theme.colors.primary : theme.colors.gray500)};
`;

const EventTitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray800};
  margin-top: 2px;
`;

const PaymentAmountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const PaymentAmount = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.gray900};
  letter-spacing: -0.01em;
`;

const PaymentTotal = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.gray200};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.success || '#10b981'};
`;

const PaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray600};
`;

const PaymentRowValue = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const DocChecklist = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
`;

const DocName = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocStatusIcon = styled.span<{ $done: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  color: ${({ theme, $done }) => ($done ? (theme.colors.success || '#10b981') : theme.colors.gray400)};

  svg {
    width: 15px;
    height: 15px;
  }
`;

// Layout components now imported from ui-components

const BookShipmentModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const BookShipmentModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const BookShipmentModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 1;
`;

const BookShipmentModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const BookShipmentModalBody = styled.div`
  padding: 24px;
`;

// Types
type TimelineEvent = {
  id: string;
  timestamp: string;
  title: string;
  source: 'Carrier' | 'Port' | 'System' | 'Customs';
  status: 'completed' | 'pending' | 'delayed';
  details: string;
};

type ShippingDocument = {
  name: string;
  type: 'bill-of-lading' | 'delivery-order' | 'arrival-notice' | 'gate-pass' | 'carrier-invoice';
  version: string;
  issuedBy: string;
  issuedDate: string;
  status: 'issued' | 'pending' | 'cancelled';
};

type CustomsStatus = {
  status: 'cleared' | 'pending' | 'not-required' | 'hold';
  clearanceDate?: string;
  authority: string;
  referenceId: string;
};

type Alert = {
  id: string;
  severity: 'info' | 'warning' | 'danger';
  title: string;
  description: string;
  timestamp: string;
};

type VesselPosition = {
  vesselName: string;
  imo: string;
  currentPosition?: string;
  lastUpdate: string;
  delayDays?: number;
};

const ORDER_SHIPMENTS_QUERY = gql`
  query ShipmentsPageOrderShipments($limit: Int = 300, $offset: Int = 0) {
    orderShipments(limit: $limit, offset: $offset) {
      id
      orderId
      shipmentNo
      shippingProvider
      shippingStatus
      trackingNumber
      location
      shippingOptions
      shippingResponse
      carrier
      packageType
      weight
      dimensions
      estimatedArrivalDate
      pickupCity
      pickupCountry
      deliveryCity
      deliveryCountry
      createdAt
      updatedAt
       contacts {
         id
         name
         organization
         role
         team
         email
         phone
         visibility
         isPrimary
         assignmentRole
       }
    }
  }
`;

const PRODUCTION_ORDERS_QUERY = gql`
  query ShipmentsPageProductionOrders($limit: Int!, $offset: Int!) {
    productionOrders(limit: $limit, offset: $offset) {
      orderId
      orderNo
      customerName
      supplier
      status
      expectedCompletionDate
      lastUpdated
      notes
    }
  }
`;

const PAYMENT_SUMMARY_QUERY = gql`
  query ShipmentsPagePaymentSummary($limit: Int = 500, $offset: Int = 0) {
    orderPayments(limit: $limit, offset: $offset) {
      id
      orderId
      amount
      paymentStatus
      currency
    }
    orders(limit: $limit, offset: $offset) {
      id
      finalAmount
      currency
    }
  }
`;

interface OrderPaymentRecord {
  id: number;
  orderId?: number | null;
  amount?: number | null;
  paymentStatus?: string | null;
  currency?: string | null;
}

interface OrderTotalRecord {
  id: number;
  finalAmount?: number | null;
  currency?: string | null;
}

type PaymentSummary = {
  total: number;
  paid: number;
  balance: number;
  currency: string;
  hasData: boolean;
};

interface ProductionOrderRecord {
  orderId: number;
  orderNo: string;
  customerName: string;
  supplier: string;
  status: string;
  expectedCompletionDate?: string | null;
  lastUpdated: string;
  notes?: string | null;
}

interface OrderShipmentRecord {
  id: number;
  orderId?: number | null;
  shipmentNo?: string | null;
  shippingProvider?: string | null;
  shippingStatus?: number | null;
  trackingNumber?: string | null;
  location?: string | null;
  shippingOptions?: string | null;
  shippingResponse?: string | null;
  carrier?: string | null;
  packageType?: string | null;
  weight?: number | null;
  dimensions?: string | null;
  estimatedArrivalDate?: string | null;
  pickupCity?: string | null;
  pickupCountry?: string | null;
  deliveryCity?: string | null;
  deliveryCountry?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  contacts?: Array<{
    id: number;
    name: string;
    organization?: string | null;
    role?: string | null;
    team?: string | null;
    email?: string | null;
    phone?: string | null;
    visibility?: string | null;
    isPrimary?: boolean | null;
    assignmentRole?: string | null;
  }>;
}

type ShipmentOrder = {
  selectionKey: string;
  backendOrderId?: number;
  orderId: string;
  customerName: string;
  supplier: string;
  shipmentId: string;
  /** Raw numeric id of the row in `order_shipments` — required for updateOrderShipment. */
  orderShipmentDbId: number;
  carrier: string;
  vessel: string;
  voyage: string;
  eta: string;
  etd: string;
  trackingNumber: string;
  location: string;
  container: string;
  productionStatus: string;
  status: 'in-transit' | 'arrived' | 'delivered' | 'pending';
  shippingStatusCode?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  shippingOptionsRaw?: Record<string, any> | null;
  shippingResponseRaw?: Record<string, any> | null;
  contacts: Contact[];
  /** Booking/logistics fields captured post order-creation via Book Shipment. */
  bookingCarrier?: string | null;
  bookingPackageType?: string | null;
  bookingWeight?: number | null;
  bookingDimensions?: string | null;
  bookingEstimatedArrivalDate?: string | null;
  bookingPickupCity?: string | null;
  bookingPickupCountry?: string | null;
  bookingDeliveryCity?: string | null;
  bookingDeliveryCountry?: string | null;
  needsBooking: boolean;
};

const normalizeContactRole = (role?: string | null): Contact['role'] => {
  switch ((role || '').toLowerCase()) {
    case 'customer':
      return 'Customer';
    case 'manufacturer':
      return 'Manufacturer';
    case 'ops owner':
    case 'ops_owner':
      return 'Ops Owner';
    case 'carrier':
      return 'Carrier';
    case 'customs broker':
    case 'customs_broker':
      return 'Customs Broker';
    case 'compliance officer':
    case 'compliance_officer':
      return 'Compliance Officer';
    case 'finance contact':
    case 'finance_contact':
      return 'Finance Contact';
    case 'authority':
      return 'Authority';
    default:
      return 'Ops Owner';
  }
};

const mapApiContactToContact = (
  shipmentKey: string,
  contact: NonNullable<OrderShipmentRecord['contacts']>[number]
): Contact => ({
  id: String(contact.id),
  name: contact.name || '',
  organization: contact.organization || undefined,
  role: normalizeContactRole(contact.role),
  email: contact.email || '',
  phone: contact.phone || undefined,
  visibility: contact.visibility === 'Customer-visible' ? 'Customer-visible' : 'Internal',
  primary: Boolean(contact.isPrimary),
  linkedShipments: [shipmentKey],
});

const parseJsonRecord = (value?: string | null): Record<string, any> | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const pickString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return undefined;
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) return 'TBD';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const formatDisplayTimestamp = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const mapShipmentStatus = (status?: number | null): ShipmentOrder['status'] => {
  if (status == null) return 'pending';
  if (status >= 7) return 'delivered';
  if (status >= 5) return 'arrived';
  if (status >= 2) return 'in-transit';
  return 'pending';
};

const normalizeTimelineSource = (source?: unknown): TimelineEvent['source'] => {
  const value = String(source || '').toLowerCase();
  if (value === 'carrier') return 'Carrier';
  if (value === 'port') return 'Port';
  if (value === 'customs') return 'Customs';
  return 'System';
};

const normalizeTimelineStatus = (status?: unknown): TimelineEvent['status'] => {
  const value = String(status || '').toLowerCase();
  if (value === 'completed') return 'completed';
  if (value === 'delayed') return 'delayed';
  return 'pending';
};

const normalizeDocumentType = (value?: unknown): ShippingDocument['type'] => {
  const text = String(value || '').toLowerCase();
  if (text.includes('bill') && text.includes('lading')) return 'bill-of-lading';
  if (text.includes('delivery')) return 'delivery-order';
  if (text.includes('arrival')) return 'arrival-notice';
  if (text.includes('gate')) return 'gate-pass';
  return 'carrier-invoice';
};

const deriveTimeline = (shipment?: ShipmentOrder): TimelineEvent[] => {
  if (!shipment) return [];

  const responseTimeline = shipment.shippingResponseRaw?.timeline;
  const optionsTimeline = shipment.shippingOptionsRaw?.timeline;
  const sourceTimeline = Array.isArray(responseTimeline)
    ? responseTimeline
    : Array.isArray(optionsTimeline)
      ? optionsTimeline
      : null;

  if (sourceTimeline) {
    return sourceTimeline.map((entry: Record<string, any>, index: number) => ({
      id: String(entry.id || `evt-${index + 1}`),
      timestamp: formatDisplayTimestamp(entry.timestamp || entry.time || entry.createdAt || shipment.updatedAt),
      title: String(entry.title || entry.event || `Event ${index + 1}`),
      source: normalizeTimelineSource(entry.source),
      status: normalizeTimelineStatus(entry.status),
      details: String(entry.details || entry.description || 'No details available'),
    }));
  }

  const eventStatus = {
    booking: shipment.createdAt ? 'completed' : 'pending',
    departed: ['in-transit', 'arrived', 'delivered'].includes(shipment.status) ? 'completed' : 'pending',
    arrived: ['arrived', 'delivered'].includes(shipment.status) ? 'completed' : 'pending',
    delivered: shipment.status === 'delivered' ? 'completed' : 'pending',
  } as const;

  return [
    {
      id: 'evt-booking',
      timestamp: formatDisplayTimestamp(shipment.createdAt),
      title: 'Booking Confirmed',
      source: 'System',
      status: eventStatus.booking,
      details: `Shipment ${shipment.shipmentId} linked to order ${shipment.orderId}.`,
    },
    {
      id: 'evt-departed',
      timestamp: shipment.etd || 'TBD',
      title: 'Vessel Departed',
      source: 'Carrier',
      status: eventStatus.departed,
      details: `Carrier ${shipment.carrier || 'N/A'} departed with voyage ${shipment.voyage || 'N/A'}.`,
    },
    {
      id: 'evt-arrival',
      timestamp: shipment.eta || 'TBD',
      title: 'Arrival at Destination Port',
      source: 'Port',
      status: eventStatus.arrived,
      details: `Current location: ${shipment.location || 'Not available'}.`,
    },
    {
      id: 'evt-delivered',
      timestamp: formatDisplayTimestamp(shipment.updatedAt),
      title: 'Delivered',
      source: 'Carrier',
      status: eventStatus.delivered,
      details: shipment.status === 'delivered' ? 'Final delivery confirmed by shipment status.' : 'Awaiting final delivery confirmation.',
    },
  ];
};

const deriveDocuments = (shipment?: ShipmentOrder): ShippingDocument[] => {
  if (!shipment) return [];
  const fromResponse = shipment.shippingResponseRaw?.documents;
  const fromOptions = shipment.shippingOptionsRaw?.documents;
  const source = Array.isArray(fromResponse)
    ? fromResponse
    : Array.isArray(fromOptions)
      ? fromOptions
      : [];

  return source.map((doc: Record<string, any>, index: number) => ({
    name: String(doc.name || doc.title || `Document ${index + 1}`),
    type: normalizeDocumentType(doc.type || doc.name),
    version: String(doc.version || 'v1'),
    issuedBy: String(doc.issuedBy || doc.issuer || shipment.carrier || 'System'),
    issuedDate: formatDisplayTimestamp(doc.issuedDate || doc.createdAt || shipment.updatedAt),
    status: String(doc.status || '').toLowerCase() === 'cancelled'
      ? 'cancelled'
      : String(doc.status || '').toLowerCase() === 'issued'
        ? 'issued'
        : 'pending',
  }));
};

const deriveCustomsStatus = (shipment?: ShipmentOrder): CustomsStatus => {
  const rawStatus = String(
    shipment?.shippingResponseRaw?.customsStatus
    || shipment?.shippingOptionsRaw?.customsStatus
    || ''
  ).toLowerCase();

  const status: CustomsStatus['status'] =
    rawStatus === 'cleared' ? 'cleared'
      : rawStatus === 'hold' ? 'hold'
        : rawStatus === 'not-required' ? 'not-required'
          : shipment?.status === 'delivered' ? 'cleared'
            : 'pending';

  return {
    status,
    clearanceDate: status === 'cleared' ? formatDisplayTimestamp(shipment?.updatedAt) : undefined,
    authority: String(shipment?.shippingResponseRaw?.customsAuthority || shipment?.shippingOptionsRaw?.customsAuthority || 'Not available'),
    referenceId: String(shipment?.shippingResponseRaw?.customsReferenceId || shipment?.shippingOptionsRaw?.customsReferenceId || 'Not available'),
  };
};

const deriveVesselPosition = (shipment?: ShipmentOrder): VesselPosition => {
  const etaRaw = shipment?.shippingResponseRaw?.eta || shipment?.shippingOptionsRaw?.eta;
  const etaDate = etaRaw ? new Date(String(etaRaw)) : null;
  const delayDays = etaDate && !Number.isNaN(etaDate.getTime())
    ? Math.max(0, Math.ceil((Date.now() - etaDate.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  return {
    vesselName: String(shipment?.vessel || 'Unassigned Vessel'),
    imo: String(shipment?.shippingResponseRaw?.imo || shipment?.shippingOptionsRaw?.imo || 'N/A'),
    currentPosition: String(shipment?.location || shipment?.shippingResponseRaw?.position || shipment?.shippingOptionsRaw?.position || 'Not available'),
    lastUpdate: formatDisplayTimestamp(shipment?.updatedAt),
    delayDays,
  };
};

const deriveAlerts = (shipment?: ShipmentOrder, customsStatus?: CustomsStatus, vesselPosition?: VesselPosition): Alert[] => {
  if (!shipment) return [];
  const alerts: Alert[] = [];

  if (!shipment.trackingNumber || shipment.trackingNumber === 'N/A') {
    alerts.push({
      id: 'tracking-missing',
      severity: 'warning',
      title: 'Tracking Number Missing',
      description: 'Shipment exists but no tracking number is available in database.',
      timestamp: formatDisplayTimestamp(shipment.updatedAt),
    });
  }

  if (customsStatus?.status === 'hold') {
    alerts.push({
      id: 'customs-hold',
      severity: 'danger',
      title: 'Customs Hold',
      description: 'Customs status indicates a hold for this shipment.',
      timestamp: formatDisplayTimestamp(shipment.updatedAt),
    });
  }

  if ((vesselPosition?.delayDays || 0) > 0 && shipment.status !== 'delivered') {
    alerts.push({
      id: 'eta-delay',
      severity: 'warning',
      title: 'ETA Delay Risk',
      description: `Estimated arrival is delayed by ${vesselPosition?.delayDays} day(s).`,
      timestamp: formatDisplayTimestamp(shipment.updatedAt),
    });
  }

  if (alerts.length === 0) {
    alerts.push({
      id: 'status-info',
      severity: 'info',
      title: `Shipment ${shipment.status.replace('-', ' ').toUpperCase()}`,
      description: 'No critical exceptions were derived from shipment database fields.',
      timestamp: formatDisplayTimestamp(shipment.updatedAt),
    });
  }

  return alerts;
};

export interface ShipmentsPageProps {
  shipmentId?: string;
  selectedOrderId?: string;
  onOrderChange?: (orderId: string) => void;
  onNavigateToCustoms?: () => void;
  onOpenMessaging?: (payload: { orderId?: string; targetContactId?: number; targetContactName?: string; targetContactEmail?: string; preferredSurface?: 'drawer' | 'widget' }) => void;
}

export const ShipmentsPage: React.FC<ShipmentsPageProps> = ({ shipmentId: controlledShipmentId, selectedOrderId: controlledOrderId, onOrderChange, onNavigateToCustoms, onOpenMessaging }) => {
  const { role, can } = useRole();
  const { data: shipmentsData, loading: isLoadingShipments, refetch: refetchShipments } = useGraphQLQuery<{ orderShipments: OrderShipmentRecord[] }>(ORDER_SHIPMENTS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
  });
  const { data: productionData } = useGraphQLQuery<{ productionOrders: ProductionOrderRecord[] }>(PRODUCTION_ORDERS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
  });
  const { data: paymentData } = useGraphQLQuery<{ orderPayments: OrderPaymentRecord[]; orders: OrderTotalRecord[] }>(PAYMENT_SUMMARY_QUERY, {
    variables: { limit: 500, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
  });

  const liveShipmentOrders = React.useMemo<ShipmentOrder[]>(() => {
    const ordersById = new Map<number, ProductionOrderRecord>();
    (productionData?.productionOrders || []).forEach((row) => ordersById.set(row.orderId, row));

    return (shipmentsData?.orderShipments || [])
      .map((shipment) => {
        const order = shipment.orderId ? ordersById.get(shipment.orderId) : undefined;
        const shippingOptions = parseJsonRecord(shipment.shippingOptions);
        const shippingResponse = parseJsonRecord(shipment.shippingResponse);
        const vessel = pickString(
          shippingOptions?.vessel,
          shippingOptions?.vesselName,
          shippingResponse?.vessel,
          shippingResponse?.vesselName,
        ) || 'Unassigned Vessel';
        const voyage = pickString(
          shippingOptions?.voyage,
          shippingOptions?.voyageNo,
          shippingResponse?.voyage,
          shippingResponse?.voyageNo,
          shippingResponse?.bookingReference,
        ) || 'N/A';
        const container = pickString(
          shippingOptions?.container,
          shippingOptions?.containerSummary,
          shippingResponse?.container,
          shippingResponse?.containerSummary,
        ) || 'N/A';
        const orderNo = order?.orderNo || (shipment.orderId ? `ORD-${shipment.orderId}` : `ORD-${shipment.id}`);
        const shipmentNo = shipment.shipmentNo || `SHP-${shipment.id}`;
        const normalizedContacts = (shipment.contacts || []).map((contact) => mapApiContactToContact(shipmentNo, contact));

        return {
          selectionKey: orderNo,
          backendOrderId: shipment.orderId ?? order?.orderId,
          orderId: orderNo,
          customerName: order?.customerName || 'Unknown Customer',
          supplier: order?.supplier || 'Unknown Supplier',
          shipmentId: shipmentNo,
          orderShipmentDbId: shipment.id,
          carrier: shipment.shippingProvider || 'Unassigned Carrier',
          vessel,
          voyage,
          eta: formatDisplayDate(pickString(shippingOptions?.eta, shippingOptions?.estimatedArrival, shippingResponse?.eta, shipment.updatedAt, order?.expectedCompletionDate)),
          etd: formatDisplayDate(pickString(shippingOptions?.etd, shippingOptions?.estimatedDeparture, shippingResponse?.etd, shipment.createdAt)),
          trackingNumber: shipment.trackingNumber || 'N/A',
          location: shipment.location || 'N/A',
          container,
          productionStatus: order?.status || 'not-started',
          status: mapShipmentStatus(shipment.shippingStatus),
          shippingStatusCode: shipment.shippingStatus,
          createdAt: shipment.createdAt,
          updatedAt: shipment.updatedAt,
          shippingOptionsRaw: shippingOptions,
          shippingResponseRaw: shippingResponse,
          contacts: normalizedContacts,
          bookingCarrier: shipment.carrier,
          bookingPackageType: shipment.packageType,
          bookingWeight: shipment.weight,
          bookingDimensions: shipment.dimensions,
          bookingEstimatedArrivalDate: shipment.estimatedArrivalDate,
          bookingPickupCity: shipment.pickupCity,
          bookingPickupCountry: shipment.pickupCountry,
          bookingDeliveryCity: shipment.deliveryCity,
          bookingDeliveryCountry: shipment.deliveryCountry,
          // Booking is considered incomplete until both a carrier and a package type
          // have been captured via Book Shipment — either being missing means the
          // action is still relevant for this row.
          needsBooking: !shipment.carrier || !shipment.packageType,
        };
      })
      .sort((left, right) => right.orderId.localeCompare(left.orderId));
  }, [productionData?.productionOrders, shipmentsData?.orderShipments]);

  const [isBookShipmentOpen, setIsBookShipmentOpen] = React.useState(false);
  const [localOrderId, setLocalOrderId] = React.useState(controlledOrderId || controlledShipmentId || '');
  const selectedOrderId = controlledOrderId ?? localOrderId;

  React.useEffect(() => {
    if (!controlledOrderId && !localOrderId && liveShipmentOrders[0]?.orderId) {
      setLocalOrderId(liveShipmentOrders[0].orderId);
    }
  }, [controlledOrderId, liveShipmentOrders, localOrderId]);

  // Sticky rail offset based on identity header height
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const railRef = React.useRef<HTMLDivElement | null>(null);
  const [railTop, setRailTop] = React.useState<number>(140);
  React.useLayoutEffect(() => {
    const h = headerRef.current?.getBoundingClientRect().height || 0;
    // Add small margin to avoid clash
    setRailTop(Math.max(16, Math.round(h) + 16));
  }, []);

  const handleOrderChange = (orderId: string) => {
    setLocalOrderId(orderId);
    onOrderChange?.(orderId);
  };

  // Find the current shipment based on selected order or shipment id.
  const currentShipment = React.useMemo(() => {
    const lookupKey = controlledShipmentId || selectedOrderId;
    return liveShipmentOrders.find((s) => s.orderId === lookupKey || s.shipmentId === lookupKey) || liveShipmentOrders[0];
  }, [controlledShipmentId, liveShipmentOrders, selectedOrderId]);

  const timeline = React.useMemo(() => deriveTimeline(currentShipment), [currentShipment]);
  const documents = React.useMemo(() => deriveDocuments(currentShipment), [currentShipment]);
  const customsStatus = React.useMemo(() => deriveCustomsStatus(currentShipment), [currentShipment]);
  const vesselPosition = React.useMemo(() => deriveVesselPosition(currentShipment), [currentShipment]);
  const alerts = React.useMemo(() => deriveAlerts(currentShipment, customsStatus, vesselPosition), [currentShipment, customsStatus, vesselPosition]);

  const paymentSummary = React.useMemo<PaymentSummary>(() => {
    const backendOrderId = currentShipment?.backendOrderId;
    if (!backendOrderId) return { total: 0, paid: 0, balance: 0, currency: 'USD', hasData: false };

    const order = (paymentData?.orders || []).find((o) => o.id === backendOrderId);
    const payments = (paymentData?.orderPayments || []).filter((p) => p.orderId === backendOrderId);

    const total = Number(order?.finalAmount || 0);
    const paid = payments
      .filter((p) => String(p.paymentStatus || '').toLowerCase() !== 'failed' && String(p.paymentStatus || '').toLowerCase() !== 'refunded')
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

    return {
      total,
      paid,
      balance: Math.max(0, total - paid),
      currency: order?.currency || payments[0]?.currency || 'USD',
      hasData: !!order || payments.length > 0,
    };
  }, [currentShipment?.backendOrderId, paymentData]);

  const shipmentId = currentShipment?.shipmentId || controlledShipmentId || 'No shipment selected';

  // Permission checks
  const canViewShipment = can('shipment.view');
  const canEditShipment = can('shipment.edit');
  const canViewDocuments = can('documents.view');
  const canSeeDiagnostics = role === 'ops';

  if (!isLoadingShipments && liveShipmentOrders.length === 0) {
    return (
      <Page>
        <Card>
          <SectionHeading icon="Package">Shipments</SectionHeading>
          <Intent>No live shipment records are available yet. Create or sync order shipments to populate this page.</Intent>
        </Card>
      </Page>
    );
  }

   // Map real API contacts from GraphQL query
   const apiContacts = currentShipment?.contacts || [];
  const shipmentContacts: ShipmentContacts = {
     customerContact: apiContacts.find((c) => (c.role || '').toLowerCase() === 'customer') || undefined,
     manufacturerContact: apiContacts.find((c) => (c.role || '').toLowerCase() === 'manufacturer') || undefined,
     carrierContact: apiContacts.find((c) => (c.role || '').toLowerCase() === 'carrier') || undefined,
     customsBroker: apiContacts.find((c) => (c.role || '').toLowerCase().includes('customs broker')) || undefined,
     opsOwner: apiContacts.find((c) => (c.role || '').toLowerCase().includes('ops owner')) || undefined,
     complianceOfficer: apiContacts.find((c) => (c.role || '').toLowerCase().includes('compliance officer')) || undefined,
   } as any;

  const toArray = (...items: (Contact | undefined)[]) => items.filter((c): c is Contact => !!c);

  const getVisibleShipmentContacts = (userRole: string, contacts: ShipmentContacts) => {
    const internal = toArray((contacts as any).opsOwner as Contact | undefined, (contacts as any).complianceOfficer as Contact | undefined);
    const external = toArray(
      contacts.customerContact,
      contacts.manufacturerContact,
      contacts.carrierContact,
      contacts.customsBroker,
    );

    switch (userRole) {
      case 'customer':
        return {
          internal: [],
          external: external.filter((c) => c.visibility === 'Customer-visible'),
        };
      case 'sales':
        return {
          internal: [],
          external: external.filter((c) => c.visibility === 'Customer-visible'),
        };
      case 'finance':
        // Shipments page: finance sees billing contacts only (none here by design)
        return { internal: [], external: [] };
      case 'ops':
      case 'admin':
      case 'compliance':
        return { internal, external };
      default:
        return { internal: [], external: external.filter((c) => c.visibility === 'Customer-visible') };
    }
  };

  const visible = getVisibleShipmentContacts(role, shipmentContacts);

  // Stepper: derived entirely from real timeline + customs status, no invented milestones.
  const stepDefs = [
    { label: 'Booking Confirmed', date: timeline[0]?.timestamp, status: timeline[0]?.status || 'pending' },
    { label: 'Vessel Departed', date: timeline[1]?.timestamp, status: timeline[1]?.status || 'pending' },
    { label: 'Arrived at Port', date: timeline[2]?.timestamp, status: timeline[2]?.status || 'pending' },
    { label: 'Customs', date: customsStatus.clearanceDate, status: customsStatus.status === 'cleared' ? 'completed' as const : customsStatus.status === 'hold' ? 'delayed' as const : 'pending' as const },
    { label: 'Delivered', date: timeline[3]?.timestamp, status: timeline[3]?.status || 'pending' },
  ];
  const firstIncompleteIndex = stepDefs.findIndex((s) => s.status !== 'completed');
  const activeStepIndex = firstIncompleteIndex === -1 ? stepDefs.length - 1 : firstIncompleteIndex;
  const steps = stepDefs.map((step, index) => ({
    ...step,
    state: (step.status === 'completed' ? 'done' : index === activeStepIndex ? 'active' : 'todo') as 'done' | 'active' | 'todo',
  }));

  // Primary alert: highest-severity item from the real alerts list, with a real action only when one exists.
  const primaryAlert = alerts.find((a) => a.severity === 'danger') || alerts.find((a) => a.severity === 'warning') || alerts[0];
  const primaryAlertAction = primaryAlert?.id === 'customs-hold' && onNavigateToCustoms
    ? { label: 'Resolve in Customs', onClick: onNavigateToCustoms }
    : null;

  const issuedDocsCount = documents.filter((d) => d.status === 'issued').length;
  const recentEvents = [...timeline].filter((e) => e.status === 'completed').reverse();

  return (
    <Page>
      <LayoutGridComponent mainRatio="calc(100% - 380px)" sideRatio="360px" gap="20px">
        <LayoutMain>
      <TopRow>
        <div>
          <Breadcrumb>
            <Crumb>Shipments</Crumb>
            <span>→</span>
            <Crumb>{shipmentId}</Crumb>
          </Breadcrumb>
          <Intent>
            Execution & tracking: what is happening, not why it was chosen.
          </Intent>
        </div>
      </TopRow>

      {/* At-a-glance summary: header, milestone stepper, top alert, payment/docs/events */}
      <HeaderBar>
        <HeaderTitleBlock>
          <HeaderStatusChip $tone={currentShipment?.status === 'delivered' ? 'delivered' : currentShipment?.status === 'arrived' ? 'arrived' : currentShipment?.status === 'in-transit' ? 'in-transit' : 'pending'}>
            {currentShipment?.status === 'in-transit' ? 'In Transit' : currentShipment?.status === 'arrived' ? 'Arrived' : currentShipment?.status === 'delivered' ? 'Delivered' : 'Pending'}
          </HeaderStatusChip>
          <HeaderTitle>{shipmentId}</HeaderTitle>
          <HeaderMeta>
            <span>{currentShipment?.supplier || 'Unknown supplier'}</span>
            <Icon name="ChevronRight" size={14} />
            <span>{currentShipment?.customerName || 'Unknown customer'}</span>
            <span>·</span>
            <span>{currentShipment?.container || 'Container N/A'}</span>
            <span>·</span>
            <span>{currentShipment?.carrier || 'Unassigned carrier'}</span>
          </HeaderMeta>
        </HeaderTitleBlock>
        {shipmentContacts.opsOwner && (
          <Button
            onClick={() => onOpenMessaging?.({
              orderId: currentShipment?.orderId,
              targetContactId: Number(shipmentContacts.opsOwner?.id || 0) || undefined,
              targetContactName: shipmentContacts.opsOwner?.name,
              targetContactEmail: shipmentContacts.opsOwner?.email,
              preferredSurface: 'drawer',
            })}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Send" size={14} /> Message Owner
            </span>
          </Button>
        )}
      </HeaderBar>

      <StepperRow>
        {steps.map((step) => (
          <StepperItem key={step.label} $state={step.state}>
            <StepCircle $state={step.state}>
              {step.state === 'done' ? <Icon name="CheckCircle" size={14} /> : null}
            </StepCircle>
            <StepText>
              <StepLabel $state={step.state}>{step.label}</StepLabel>
              <StepMeta>{step.date || (step.state === 'todo' ? 'Upcoming' : '—')}</StepMeta>
            </StepText>
          </StepperItem>
        ))}
      </StepperRow>

      {primaryAlert && (
        <PrimaryAlertBanner $severity={primaryAlert.severity}>
          <PrimaryAlertLeft>
            <PrimaryAlertIcon $severity={primaryAlert.severity}>
              <Icon name={primaryAlert.severity === 'danger' ? 'AlertTriangle' : primaryAlert.severity === 'warning' ? 'AlertCircle' : 'Info'} size={16} />
            </PrimaryAlertIcon>
            <PrimaryAlertText>
              <strong>{primaryAlert.title}</strong> — {primaryAlert.description}
            </PrimaryAlertText>
          </PrimaryAlertLeft>
          {primaryAlertAction && (
            <Button size="sm" variant="outline" onClick={primaryAlertAction.onClick}>
              {primaryAlertAction.label}
            </Button>
          )}
        </PrimaryAlertBanner>
      )}

      <SummaryGrid>
        <SummaryCard>
          <SummaryCardTitle>Latest Events</SummaryCardTitle>
          {recentEvents.length === 0 ? (
            <Muted>No events recorded yet — this shipment hasn't progressed past booking.</Muted>
          ) : (
            <EventList>
              {recentEvents.map((event, index) => (
                <EventItem key={event.id} $isFirst={index === 0}>
                  <EventDate $isFirst={index === 0}>{event.timestamp}</EventDate>
                  <EventTitle>{event.title}</EventTitle>
                </EventItem>
              ))}
            </EventList>
          )}
        </SummaryCard>

        <SummaryCard>
          <SummaryCardTitle>Payment</SummaryCardTitle>
          {paymentSummary.hasData ? (
            <>
              <PaymentAmountRow>
                <PaymentAmount>{paymentSummary.currency} {paymentSummary.paid.toLocaleString()}</PaymentAmount>
                <PaymentTotal>/ {paymentSummary.total.toLocaleString()}</PaymentTotal>
              </PaymentAmountRow>
              <ProgressTrack>
                <ProgressFill $pct={paymentSummary.total > 0 ? (paymentSummary.paid / paymentSummary.total) * 100 : 0} />
              </ProgressTrack>
              <PaymentRow>
                <span>Balance due</span>
                <PaymentRowValue>{paymentSummary.currency} {paymentSummary.balance.toLocaleString()}</PaymentRowValue>
              </PaymentRow>
              <Muted>Full payment history and receipts are in Payments.</Muted>
            </>
          ) : (
            <Muted>No payment records linked to this order yet.</Muted>
          )}
        </SummaryCard>

        <SummaryCard>
          <SummaryCardTitle>
            Documents
            <SummaryCardCount>{issuedDocsCount} / {documents.length}</SummaryCardCount>
          </SummaryCardTitle>
          <DocChecklist>
            {documents.length === 0 && <Muted>No documents found yet.</Muted>}
            {documents.map((doc) => (
              <DocRow key={doc.name}>
                <DocName>{doc.name}</DocName>
                <DocStatusIcon $done={doc.status === 'issued'}>
                  <Icon name={doc.status === 'issued' ? 'CheckCircle' : 'Clock'} size={15} />
                </DocStatusIcon>
              </DocRow>
            ))}
          </DocChecklist>
        </SummaryCard>
      </SummaryGrid>

      {canViewShipment && !canEditShipment && (
        <AlertBox $severity="warning">
          <AlertTitle><Icon name="Lock" size={15} /> Read-Only Access</AlertTitle>
          <div>Only Ops can manage and edit shipment details. You have view-only access.</div>
        </AlertBox>
      )}

      {/* Order Selection */}
      <Card>
        <SectionHeading icon="Package">Select Shipment</SectionHeading>
        <Inline style={{ justifyContent: 'space-between' }}>
          <div style={{ maxWidth: '520px', width: '100%' }}>
            <SearchableSelect
              options={liveShipmentOrders.map((order) => `${order.orderId} — ${order.customerName} — ${order.carrier} → ${order.status.toUpperCase()}`)}
              value={selectedOrderId}
              onChange={(value) => {
                const orderId = value.split(' — ')[0];
                handleOrderChange(orderId);
              }}
              placeholder="Search shipments by order ID, customer, carrier, or status..."
            />
          </div>
          {canEditShipment && currentShipment?.needsBooking && (
            <Button size="sm" onClick={() => setIsBookShipmentOpen(true)}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="Package" size={14} /> Book Shipment
              </span>
            </Button>
          )}
        </Inline>
        <Grid>
          <div>
            <Label>Order ID</Label>
            <Value>{currentShipment?.orderId || 'N/A'}</Value>
          </div>
          <div>
            <Label>Shipment ID</Label>
            <Value>{currentShipment?.shipmentId || 'N/A'}</Value>
          </div>
          <div>
            <Label>Customer</Label>
            <Value>{currentShipment?.customerName || 'Unknown Customer'}</Value>
          </div>
          <div>
            <Label>Supplier</Label>
            <Value>{currentShipment?.supplier || 'Unknown Supplier'}</Value>
          </div>
          <div>
            <Label>Carrier</Label>
            <Value>{currentShipment?.carrier || 'Unassigned Carrier'}</Value>
          </div>
          <div>
            <Label>Tracking Number</Label>
            <Value>{currentShipment?.trackingNumber || 'N/A'}</Value>
          </div>
        </Grid>
      </Card>

      {/* A. Shipment Identity & Summary */}
      <StickyHeader ref={headerRef}>
        <SectionHeading icon="Package">Shipment Identity & Summary</SectionHeading>
        <HeaderGrid>
          <div>
            <Label>Shipment ID</Label>
            <Value>{currentShipment?.shipmentId || 'N/A'}</Value>
          </div>
          <div>
            <Label>Linked Order</Label>
            <Value>{currentShipment?.orderId || 'N/A'}</Value>
          </div>
          <div>
            <Label>Customer</Label>
            <Value>{currentShipment?.customerName || 'Unknown Customer'}</Value>
          </div>
          <div>
            <Label>Supplier</Label>
            <Value>{currentShipment?.supplier || 'Unknown Supplier'}</Value>
          </div>
          <div>
            <Label>Carrier</Label>
            <Value>{currentShipment?.carrier || 'Unassigned Carrier'}</Value>
          </div>
          <div>
            <Label>Vessel</Label>
            <Value>{currentShipment?.vessel || 'Unassigned Vessel'}</Value>
          </div>
          <div>
            <Label>Voyage</Label>
            <Value>{currentShipment?.voyage || 'N/A'}</Value>
          </div>
          <div>
            <Label>Container</Label>
            <Value>{currentShipment?.container || 'N/A'}</Value>
          </div>
          <div>
            <Label>ETD</Label>
            <Value>{currentShipment?.etd || 'TBD'}</Value>
          </div>
          <div>
            <Label>ETA</Label>
            <Value>{currentShipment?.eta || 'TBD'}</Value>
          </div>
          <div>
            <Label>Tracking Number</Label>
            <Value>{currentShipment?.trackingNumber || 'N/A'}</Value>
          </div>
          <div>
            <Label>Current Location</Label>
            <Value>{currentShipment?.location || 'N/A'}</Value>
          </div>
          <div>
            <Label>Production Status</Label>
            <Value>{currentShipment?.productionStatus || 'not-started'}</Value>
          </div>
          <div>
            <Label>Current Status</Label>
            <Pill $tone={currentShipment?.status === 'delivered' ? 'delivered' : currentShipment?.status === 'arrived' ? 'arrived' : currentShipment?.status === 'in-transit' ? 'in-transit' : 'pending'}>
              {currentShipment?.status === 'in-transit' && <><Icon name="Ship" size={12} /> In Transit</>}
              {currentShipment?.status === 'arrived' && <><Icon name="Package" size={12} /> Arrived</>}
              {currentShipment?.status === 'delivered' && <><Icon name="CheckCircle" size={12} /> Delivered</>}
              {currentShipment?.status === 'pending' && <><Icon name="Clock" size={12} /> Pending</>}
            </Pill>
          </div>
        </HeaderGrid>
        <Inline>
          <span style={{ fontSize: 13, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="Users" size={14} /> People involved: {visible.internal.length + visible.external.length}
          </span>
          <Button size="sm" variant="ghost" onClick={() => railRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            View people →
          </Button>
        </Inline>
      </StickyHeader>

      {/* F. Exceptions & Alerts */}
      {alerts.length > 0 && (
        <Card>
          <SectionHeading icon="AlertTriangle">Exceptions & Alerts</SectionHeading>
          {alerts.map((alert) => (
            <AlertBox key={alert.id} $severity={alert.severity}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <AlertTitle>{alert.title}</AlertTitle>
                <Badge $variant={alert.severity === 'danger' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}>
                  {alert.timestamp}
                </Badge>
              </div>
              <div style={{ fontSize: '14px', color: '#4b5563' }}>{alert.description}</div>
            </AlertBox>
          ))}
        </Card>
      )}

      {/* B. Shipment Timeline (CORE) */}
      <Card>
        <SectionHeading icon="Calendar">Shipment Timeline</SectionHeading>
        <Muted>Actual execution events — this is tracking truth.</Muted>
        <Divider />
        <TwoCol>
          {timeline.length === 0 && (
            <TimelineItem>
              <TimelineHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <TimelineTitle>No Timeline Events</TimelineTitle>
                  <TimelineLabel>DATABASE</TimelineLabel>
                </div>
                <Pill $tone="pending"><Icon name="Clock" size={12} /> PENDING</Pill>
              </TimelineHeader>
              <TimelineValue>
                <Muted>No shipment timeline events were found in database fields for this shipment.</Muted>
              </TimelineValue>
            </TimelineItem>
          )}
          {timeline
            .filter((event) => {              // Filter based on role - customer and sales see major milestones only
              if (role === 'customer' || role === 'sales') {
                return ['Booking Confirmed', 'Vessel Departed', 'Arrival at Destination Port', 'Delivered'].includes(event.title);
              }
              // Ops, compliance, admin, finance see everything
              return true;
            })
            .map((event) => (
              <TimelineItem key={event.id}>
                <TimelineHeader>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <TimelineTitle>{event.title}</TimelineTitle>
                    <TimelineLabel>{event.timestamp}</TimelineLabel>
                  </div>
                  <Pill $tone={event.status === 'completed' ? 'delivered' : event.status === 'delayed' ? 'delayed' : 'pending'}>
                    {event.status === 'completed' && <><Icon name="CheckCircle" size={12} /> COMPLETED</>}
                    {event.status === 'pending' && <><Icon name="Clock" size={12} /> PENDING</>}
                    {event.status === 'delayed' && <><Icon name="AlertTriangle" size={12} /> DELAYED</>}
                  </Pill>
                </TimelineHeader>
                <TimelineValue>
                  <Muted>Source: {event.source}</Muted>
                  <div>{event.details}</div>
                </TimelineValue>
              </TimelineItem>
            ))}
        </TwoCol>
      </Card>


      {/* C. Vessel & Tracking Status */}
      <Card>
        <SectionHeading icon="Ship">Vessel & Tracking Status</SectionHeading>
        <Muted>Accuracy over visuals — no overpromising "real-time"</Muted>
        <Divider />
        <Grid>
          <div>
            <Label>Vessel Name</Label>
            <Value>{vesselPosition.vesselName}</Value>
          </div>
          <div>
            <Label>IMO</Label>
            <Value>{vesselPosition.imo}</Value>
          </div>
          <div>
            <Label>Current Position</Label>
            <Value>{vesselPosition.currentPosition || 'Not available'}</Value>
          </div>
          <div>
            <Label>Last Update</Label>
            <Value>{vesselPosition.lastUpdate}</Value>
          </div>
          <div>
            <Label>Delay vs Original ETA</Label>
            <Value>
              {vesselPosition.delayDays === 0 ? (
                <Badge $variant="success">On Schedule</Badge>
              ) : (
                <Badge $variant="warning">{vesselPosition.delayDays} days delayed</Badge>
              )}
            </Value>
          </div>
        </Grid>
        <Muted style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="Info" size={13} /> Position data sourced from carrier AIS feeds. Updates may be delayed by 2-6 hours.
        </Muted>
      </Card>

      {/* E. Customs Status (Summary Only) */}
      <Card>
        <SectionHeading icon="ShieldCheck">Customs Clearance Status</SectionHeading>
        <Muted>Summary only — deep work lives in Customs page</Muted>
        <Divider />
        <Grid>
          <div>
            <Label>Customs Status</Label>
            <Value>
              {customsStatus.status === 'cleared' && <Pill $tone="cleared"><Icon name="CheckCircle" size={12} /> CLEARED</Pill>}
              {customsStatus.status === 'pending' && <Pill $tone="pending"><Icon name="Clock" size={12} /> PENDING</Pill>}
              {customsStatus.status === 'hold' && <Pill $tone="delayed"><Icon name="AlertTriangle" size={12} /> CUSTOMS HOLD</Pill>}
              {customsStatus.status === 'not-required' && <Badge>Not Required</Badge>}
            </Value>
          </div>
          {customsStatus.clearanceDate && (
            <div>
              <Label>Clearance Date</Label>
              <Value>{customsStatus.clearanceDate}</Value>
            </div>
          )}
          <div>
            <Label>Authority</Label>
            <Value>{customsStatus.authority}</Value>
          </div>
          <div>
            <Label>Reference ID</Label>
            <Value>{customsStatus.referenceId}</Value>
          </div>
        </Grid>
        <Inline style={{ marginTop: 12 }}>
          <Button 
            size="sm" 
            variant="ghost" 
            style={{ background: '#e0f2fe', color: '#075985', border: 'none' }} 
            onClick={onNavigateToCustoms}
            disabled={!canViewShipment}
            title={!canViewShipment ? 'You do not have permission to view customs cases' : ''}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Search" size={14} /> View Customs Case →
            </span>
          </Button>
          <Muted>Query handling, amendments, and document uploads managed in Customs page only.</Muted>
        </Inline>
      </Card>

      {/* D. Final Shipping Documents */}
      <Card>
        <SectionHeading icon="FileText">Final Shipping Documents</SectionHeading>
        <Muted>Execution artifacts — generated/issued by carrier or system</Muted>
        <Divider />
        <Table>
          <TableHeader>Document</TableHeader>
          <TableHeader>Version</TableHeader>
          <TableHeader>Issued By</TableHeader>
          <TableHeader>Issued Date</TableHeader>
          {documents.length === 0 && (
            <TableRow>
              <Value>No shipping documents found</Value>
              <Value>—</Value>
              <Value>Database</Value>
              <Value>—</Value>
            </TableRow>
          )}
          {documents.map((doc) => (
            <TableRow key={doc.name}>
              <Value>
                {doc.name}
                {doc.status === 'issued' && (
                  <Badge $variant="success" style={{ marginLeft: 8 }}>Issued</Badge>
                )}
                {doc.status === 'pending' && (
                  <Badge $variant="warning" style={{ marginLeft: 8 }}>Pending</Badge>
                )}
              </Value>
              <Value>{doc.version}</Value>
              <Value>{doc.issuedBy}</Value>
              <Value>{doc.issuedDate}</Value>
            </TableRow>
          ))}
        </Table>
        <Inline style={{ marginTop: 12 }}>
          <Button 
            size="sm" 
            variant="ghost"
            disabled={!canViewDocuments}
            title={!canViewDocuments ? 'You do not have permission to download documents' : ''}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Download" size={14} /> Download All Documents
            </span>
          </Button>
          <Muted>All documents are read-only, versioned, and immutable.</Muted>
        </Inline>
      </Card>

      {/* Ops-only: Diagnostics */}
      {canSeeDiagnostics && (
        <Card>
          <SectionHeading icon="Settings">Delay Diagnostics (Ops Only)</SectionHeading>
          <div style={{ padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <Muted>
              • Shipment status: {currentShipment?.status || 'pending'}<br />
              • Delay days: {vesselPosition.delayDays || 0}<br />
              • Customs status: {customsStatus.status}<br />
              • Active alerts: {alerts.length}
            </Muted>
          </div>
        </Card>
      )}
        </LayoutMain>

        {/* Right Context Rail */}
        <LayoutSide $top={railTop} ref={railRef}>
          <CardContainer variant="elevated" padding="md">
            <SectionHeading icon="Users">People Involved</SectionHeading>
            <Muted style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <span>
                Who is involved — visibility depends on your role. Legend:{' '}
                <Icon name="Lock" size={12} /> Internal → internal users only,{' '}
                <Icon name="User" size={12} /> Customer-visible → visible to customer portal.
              </span>
            </Muted>
            <Divider />
            {/* Owner */}
            {shipmentContacts.opsOwner && (
              <div style={{
                marginBottom: 12,
                padding: '10px 12px',
                border: alerts.some(a => a.severity === 'danger') ? '1px solid #fca5a5' : '1px dashed #e5e7eb',
                borderRadius: 8,
                background: alerts.some(a => a.severity === 'danger') ? '#fee2e2' : '#f9fafb'
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Shipment Owner</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {alerts.some(a => a.severity === 'danger') && <Icon name="AlertTriangle" size={14} />}
                    {shipmentContacts.opsOwner.name} <span style={{ fontWeight: 600, color: '#6b7280' }}>(Ops)</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onOpenMessaging?.({
                      orderId: currentShipment?.orderId,
                      targetContactId: Number(shipmentContacts.opsOwner?.id || 0) || undefined,
                      targetContactName: shipmentContacts.opsOwner?.name,
                      targetContactEmail: shipmentContacts.opsOwner?.email,
                      preferredSurface: 'drawer',
                    })}
                  >
                    Message
                  </Button>
                </div>
              </div>
            )}

            <SectionHeading size="sm">Internal</SectionHeading>
            <ContactPanel
              title="Internal"
              icon="Building2"
              contacts={visible.internal}
              onContactAction={(contact, action) => {
                if (action === 'message') {
                  onOpenMessaging?.({
                    orderId: currentShipment?.orderId,
                    targetContactId: Number(contact.id || 0) || undefined,
                    targetContactName: contact.name,
                    targetContactEmail: contact.email,
                    preferredSurface: 'drawer',
                  });
                }
              }}
            />

            <SectionHeading size="sm" style={{ marginTop: 12 }}>External</SectionHeading>
            <ContactPanel
              title="External"
              icon="Globe"
              contacts={visible.external}
              onContactAction={(contact, action) => {
                if (action === 'message') {
                  onOpenMessaging?.({
                    orderId: currentShipment?.orderId,
                    targetContactId: Number(contact.id || 0) || undefined,
                    targetContactName: contact.name,
                    targetContactEmail: contact.email,
                    preferredSurface: 'drawer',
                  });
                }
              }}
            />
          </CardContainer>
        </LayoutSide>
      </LayoutGridComponent>

      {isBookShipmentOpen && currentShipment && (
        <BookShipmentModalOverlay onClick={() => setIsBookShipmentOpen(false)}>
          <BookShipmentModalContent onClick={(e) => e.stopPropagation()}>
            <BookShipmentModalHeader>
              <BookShipmentModalTitle>Book Shipment — {currentShipment.orderId}</BookShipmentModalTitle>
              <Button size="sm" variant="ghost" onClick={() => setIsBookShipmentOpen(false)}>
                <Icon name="X" size={16} />
              </Button>
            </BookShipmentModalHeader>
            <BookShipmentModalBody>
              <BookShipment
                orderShipmentId={currentShipment.orderShipmentDbId}
                orderLabel={currentShipment.orderId}
                pickupCity={currentShipment.bookingPickupCity || undefined}
                pickupCountry={currentShipment.bookingPickupCountry || undefined}
                deliveryCity={currentShipment.bookingDeliveryCity || undefined}
                deliveryCountry={currentShipment.bookingDeliveryCountry || undefined}
                initialCarrier={currentShipment.bookingCarrier || undefined}
                initialPackageType={currentShipment.bookingPackageType || undefined}
                initialWeight={currentShipment.bookingWeight}
                initialDimensions={currentShipment.bookingDimensions || undefined}
                initialEstimatedArrivalDate={currentShipment.bookingEstimatedArrivalDate || undefined}
                onClose={() => setIsBookShipmentOpen(false)}
                onBooked={() => refetchShipments()}
              />
            </BookShipmentModalBody>
          </BookShipmentModalContent>
        </BookShipmentModalOverlay>
      )}
    </Page>
  );
};
